<template>
  <div class="widget-carousel-preview card border-0 shadow-sm">
    <div class="card-header bg-white border-bottom widget-carousel-preview__header">
      <strong>Preview</strong>
    </div>

    <div class="card-body widget-carousel-preview__body">
      <div class="widget-carousel-preview__content-frame">
        <div v-if="displayTitle" class="widget-carousel-preview__title">
          {{ displayTitle }}
        </div>

        <div v-if="displaySubtitle" class="widget-carousel-preview__subtitle">
          {{ displaySubtitle }}
        </div>

        <div class="widget-carousel-preview__carousel-wrapper">
          <div
            :id="carouselId"
            class="carousel slide"
            :data-ride="form.auto_scroll && hasProducts ? 'carousel' : false"
          >
            <ol
              v-if="form.display_page_indicator && hasProducts"
              class="carousel-indicators widget-carousel-preview__indicators"
            >
              <li
                v-for="(product, index) in previewProducts"
                :key="product.local_id"
                :data-target="'#' + carouselId"
                :data-slide-to="index"
                :class="{ active: index === 0 }"
              />
            </ol>

            <div class="carousel-inner">
              <div
                v-for="(product, index) in previewProducts"
                :key="product.local_id"
                class="carousel-item"
                :class="{ active: index === 0 }"
              >
                <div class="widget-carousel-preview__card-shell">
                  <div
                    class="widget-carousel-preview__ratio-box"
                    :style="ratioBoxStyle"
                  >
                    <div class="widget-carousel-preview__ratio-content">
                      <div
                        class="widget-carousel-preview__slide-card"
                        :class="{
                          'widget-carousel-preview__slide-card--d-shape': product.use_d_shape
                        }"
                        :style="getSlideCardStyle(product)"
                      >
                        <div
                          v-if="!product.is_active"
                          class="widget-carousel-preview__inactive-overlay"
                        >
                          <div class="widget-carousel-preview__inactive-badge">
                            Produs indisponibil
                          </div>

                          <div
                            v-if="product.reason"
                            class="widget-carousel-preview__inactive-reason"
                          >
                            {{ product.reason }}
                          </div>
                        </div>

                        <div
                          class="widget-carousel-preview__slide-background"
                          :style="getSlideBackgroundStyle(product)"
                        >
                          <div
                            v-if="!hasAnySlideBackground(product)"
                            class="widget-carousel-preview__slide-background-placeholder"
                          >
                            No background
                          </div>
                        </div>

                        <div
                          v-for="position in badgePositions"
                          :key="position.key"
                          class="widget-carousel-preview__badge-position"
                          :class="'widget-carousel-preview__badge-position--' + position.key"
                        >
                          <div
                            v-for="badge in getBadgeList(product, position.key)"
                            :key="badge.local_id"
                            class="widget-carousel-preview__badge"
                            :class="{
                              'widget-carousel-preview__badge--d-shape': badge.use_d_shape
                            }"
                            :style="getBadgeStyle(badge)"
                          >
                            <template v-if="badge.image">
                              <img
                                :src="badge.image"
                                alt="badge"
                                class="widget-carousel-preview__badge-image"
                              >
                            </template>

                            <template v-else>
                              <div
                                class="widget-carousel-preview__badge-text"
                                :style="{ color: badge.title_color || '#000000' }"
                              >
                                {{ getBadgeText(product, badge) }}
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!hasProducts" class="carousel-item active">
                <div class="widget-carousel-preview__card-shell">
                  <div
                    class="widget-carousel-preview__ratio-box"
                    :style="ratioBoxStyle"
                  >
                    <div class="widget-carousel-preview__ratio-content">
                      <div class="widget-carousel-preview__empty-card">
                        <div class="widget-carousel-preview__empty-card-badge">
                          {{ ratioLabel }}
                        </div>

                        <div class="widget-carousel-preview__empty-card-body">
                          Adaugă produse pentru a vedea preview-ul complet.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <a
              v-if="hasProducts && previewProducts.length > 1"
              class="carousel-control-prev"
              :href="'#' + carouselId"
              role="button"
              data-slide="prev"
            >
              <span class="carousel-control-prev-icon" aria-hidden="true"></span>
              <span class="sr-only">Previous</span>
            </a>

            <a
              v-if="hasProducts && previewProducts.length > 1"
              class="carousel-control-next"
              :href="'#' + carouselId"
              role="button"
              data-slide="next"
            >
              <span class="carousel-control-next-icon" aria-hidden="true"></span>
              <span class="sr-only">Next</span>
            </a>
          </div>
        </div>

        <div v-if="displayFooter" class="widget-carousel-preview__footer">
          {{ displayFooter }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WidgetCarouselPreview',

  props: {
    form: {
      type: Object,
      default() {
        return {
          is_active: true,
          widget_id: '',
          tracking: '',
          available_start_date: '',
          available_end_date: '',
          ratio: 2,
          auto_scroll: true,
          display_page_indicator: true,
          title: { ro: '', en: '' },
          subtitle: { ro: '', en: '' },
          footer: { ro: '', en: '' },
          global_background_image: {
            file: null,
            preview: '',
            error: ''
          },
          products: []
        };
      }
    }
  },

  computed: {
    displayTitle() {
      return this.form.title.ro || this.form.title.en || '';
    },

    displaySubtitle() {
      return this.form.subtitle.ro || this.form.subtitle.en || '';
    },

    displayFooter() {
      return this.form.footer.ro || this.form.footer.en || '';
    },

    ratioLabel() {
      return `${this.form.ratio}:1`;
    },

    ratioPaddingTop() {
      const safeRatio = Number(this.form.ratio) || 2;
      return `${100 / safeRatio}%`;
    },

    ratioBoxStyle() {
      return {
        paddingTop: this.ratioPaddingTop
      };
    },

    carouselId() {
      return `widget-carousel-preview-${this._uid}`;
    },

    previewProducts() {
      return Array.isArray(this.form.products) ? this.form.products : [];
    },

    hasProducts() {
      return this.previewProducts.length > 0;
    },

    hasGlobalBackground() {
      return !!(
        this.form.global_background_image &&
        this.form.global_background_image.preview
      );
    },

    badgePositions() {
      return [
        { key: 'top_left', label: 'Top left' },
        { key: 'top_right', label: 'Top right' },
        { key: 'bottom_left', label: 'Bottom left' },
        { key: 'bottom_right', label: 'Bottom right' }
      ];
    }
  },

  methods: {

    resolveProductVariable(product, variableKey) {
      const safeProduct = product || {};

      const variables = {
        product_name: safeProduct.product_name || '',
        price: safeProduct.price || '',
        discount_price: safeProduct.discount_price || '',
        discount: safeProduct.discount || '',
        brand_name: safeProduct.brand_name || ''
      };

      return variables[variableKey] || '';
    },

    interpolateTemplate(template, product) {
      return String(template || '').replace(/\{\{(.*?)\}\}/g, (_, rawKey) => {
        const key = String(rawKey || '').trim();
        return this.resolveProductVariable(product, key);
      });
    },

    getBadgeText(product, badge) {
      if (!badge) {
        return '';
      }

      if (badge.content_mode === 'product_variable') {
        return this.resolveProductVariable(product, badge.product_variable_key);
      }

      const title = badge.title || { ro: '', en: '' };
      const template = title.ro || title.en || '';

      return this.interpolateTemplate(template, product);
    },
    normalizeColor(value, fallback = '#ffffff') {
      return value && String(value).trim() ? value : fallback;
    },

    getProductItems(product) {
      return Array.isArray(product.items) ? product.items : [];
    },

    getBadgeList(product, positionKey) {
      const firstItem = this.getProductItems(product)[0];
      if (!firstItem || !firstItem.badges) {
        return [];
      }

      return Array.isArray(firstItem.badges[positionKey]) ? firstItem.badges[positionKey] : [];
    },

    getSlideCardStyle(product) {
      return {
        backgroundColor: this.normalizeColor(product.background_color, 'transparent'),
        borderRadius: `${Number(product.corner_radius) || 12}px`
      };
    },

    hasAnySlideBackground(product) {
      return !!(this.getSlideBackgroundImage(product) || (product.background_color && product.background_color.trim()));
    },

    getSlideBackgroundImage(product) {
      if (this.hasGlobalBackground) {
        return this.form.global_background_image.preview;
      }

      if (product && product.image && product.image.preview) {
        return product.image.preview;
      }

      return '';
    },

    getSlideBackgroundStyle(product) {
      const backgroundImage = this.getSlideBackgroundImage(product);

      if (backgroundImage) {
        return {
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: this.normalizeColor(product.background_color, 'transparent')
        };
      }

      return {
        backgroundColor: this.normalizeColor(product.background_color, '#f1f3f5')
      };
    },

    getBadgeStyle(badge) {
      const paddings = badge.paddings || {};
      const margins = badge.margins || {};

      return {
        backgroundColor: this.normalizeColor(badge.background_color, '#FFFFFF'),
        borderRadius: `${Number(badge.corner_radius) || 12}px`,
        paddingTop: `${Number(paddings.top) || 0}px`,
        paddingLeft: `${Number(paddings.left) || 0}px`,
        paddingRight: `${Number(paddings.right) || 0}px`,
        paddingBottom: `${Number(paddings.bottom) || 0}px`,

        marginTop: `${Number(margins.top) || 0}px`,
        marginLeft: `${Number(margins.left) || 0}px`,
        marginRight: `${Number(margins.right) || 0}px`,
        marginBottom: `${Number(margins.bottom) || 0}px`
      };
    }
  }
};
</script>

<style scoped>
.widget-carousel-preview__header {
  padding: 10px 14px;
  font-size: 14px;
}

.widget-carousel-preview__body {
  padding: 14px;
}

.widget-carousel-preview__content-frame {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
}

.widget-carousel-preview__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 6px;
  color: #212529;
  text-align: left;
}

.widget-carousel-preview__subtitle {
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 12px;
  color: #6c757d;
  text-align: left;
}

.widget-carousel-preview__title,
.widget-carousel-preview__subtitle,
.widget-carousel-preview__footer,
.widget-carousel-preview__card-shell {
  padding-left: 18px;
  padding-right: 18px;
}

.widget-carousel-preview__carousel-wrapper {
  position: relative;
}

.widget-carousel-preview__ratio-box {
  position: relative;
  width: 100%;
}

.widget-carousel-preview__ratio-content {
  position: absolute;
  inset: 0;
}

.widget-carousel-preview__slide-card,
.widget-carousel-preview__empty-card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.widget-carousel-preview__slide-card--d-shape {
  border-top-right-radius: 48px !important;
  border-bottom-right-radius: 48px !important;
}

.widget-carousel-preview__slide-background {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.widget-carousel-preview__slide-background-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6c757d;
  background: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.06),
    rgba(0, 0, 0, 0.06) 8px,
    rgba(0, 0, 0, 0.02) 8px,
    rgba(0, 0, 0, 0.02) 16px
  );
}

.widget-carousel-preview__badge-position {
  position: absolute;
  z-index: 4;
  max-width: 45%;
  display: flex;
  flex-direction: column;
}

.widget-carousel-preview__badge-position--top_left {
  top: 0;
  left: 0;
  align-items: flex-start;
}

.widget-carousel-preview__badge-position--top_right {
  top: 0;
  right: 0;
  align-items: flex-end;
}

.widget-carousel-preview__badge-position--bottom_left {
  bottom: 0;
  left: 0;
  align-items: flex-start;
}

.widget-carousel-preview__badge-position--bottom_right {
  bottom: 0;
  right: 0;
  align-items: flex-end;
}

.widget-carousel-preview__badge {
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  pointer-events: none;
}

.widget-carousel-preview__badge--d-shape {
  border-top-right-radius: 24px !important;
  border-bottom-right-radius: 24px !important;
}

.widget-carousel-preview__badge-image {
  display: block;
  max-width: 100%;
  height: auto;
}

.widget-carousel-preview__badge-text {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  white-space: pre-line;
  word-break: break-word;
}

.widget-carousel-preview__empty-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #eef1f4 100%);
  border-radius: 12px;
}

.widget-carousel-preview__empty-card-badge {
  align-self: flex-start;
  margin-bottom: 10px;
  font-size: 10px;
  font-weight: 700;
  color: #495057;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  padding: 3px 8px;
}

.widget-carousel-preview__empty-card-body {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.45;
}

.widget-carousel-preview__footer {
  font-size: 11px;
  line-height: 1.4;
  margin-top: 14px;
  color: #6c757d;
  text-align: left;
}

.widget-carousel-preview__indicators {
  bottom: -50px;
}

.widget-carousel-preview__indicators li {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #6c757d;
}

.carousel-control-prev,
.carousel-control-next {
  width: 28px;
}

.carousel-control-prev-icon,
.carousel-control-next-icon {
  background-size: 14px 14px;
  filter: invert(36%);
}

.widget-carousel-preview__inactive-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  padding: 10px;
  background: rgba(255, 255, 255, 0.42);
  pointer-events: none;
}

.widget-carousel-preview__inactive-badge {
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #dc3545;
  border-radius: 999px;
}

.widget-carousel-preview__inactive-reason {
  max-width: 70%;
  margin-top: 8px;
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.35;
  color: #842029;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 8px;
  text-align: right;
}

@media (max-width: 767.98px) {
  .widget-carousel-preview__title,
  .widget-carousel-preview__subtitle,
  .widget-carousel-preview__footer,
  .widget-carousel-preview__card-shell {
    padding-left: 10px;
    padding-right: 10px;
  }

  .widget-carousel-preview__badge-text {
    font-size: 12px;
  }

  .widget-carousel-preview__badge-position {
    max-width: 70%;
  }
}
</style>
